// Aranese (oc-aran) names of days and months, applied to the text produced by Intl.DateTimeFormat.
//
// Why this is needed: browsers have no usable Aranese date data. Depending on the browser, Intl.DateTimeFormat("oc")
// resolves to Spanish (Chromium: "lunes", "abril de 2026"), or to standard Occitan (Firefox/Node: "diluns",
// "de genièr"), and Catalan or English names can appear too. All of them are mapped to the Aranese names used by
// the bulletin server (albina-server LanguageCode.oc), so website, PDF and e-mail agree:
//   "dimenge, 26 d’abriu deth 2026"   (d’ before a vowel, "de " otherwise; "deth" before the year)
//
// This module has no imports so that it can be unit-tested on its own (tests/aranes.spec.ts).

const WEEKDAYS: Record<string, string[]> = {
  deluns: ["lunes", "dilluns", "monday", "diluns"],
  dimars: ["martes", "dimarts", "tuesday", "dimars"],
  dimèrcles: ["miércoles", "dimecres", "wednesday", "dimècres"],
  dijaus: ["jueves", "dijous", "thursday", "dijòus"],
  diuendres: ["viernes", "divendres", "friday"],
  dissabte: ["sábado", "dissabte", "saturday"],
  dimenge: ["domingo", "diumenge", "sunday", "dimenge"]
};

// Month names as in the official Diccionari der aranés (IEA-AALO), lowercase as in Aranese orthography.
const MONTHS: Record<string, string[]> = {
  gèr: ["enero", "gener", "january", "genièr"],
  hereuèr: ["febrero", "febrer", "february", "febrièr"],
  març: ["marzo", "març", "march"],
  abriu: ["abril", "april"],
  mai: ["mayo", "maig", "may", "mai"],
  junh: ["junio", "juny", "june", "junh"],
  junhsèga: ["julio", "juliol", "july", "julhet"],
  agost: ["agosto", "agost", "august"],
  seteme: ["septiembre", "setembre", "september"],
  octobre: ["octubre", "octòbre", "october"],
  noveme: ["noviembre", "novembre", "november"],
  deseme: ["diciembre", "desembre", "december", "decembre"]
};

const ARANESE_BY_SOURCE = new Map<string, string>();
for (const table of [WEEKDAYS, MONTHS]) {
  for (const [aranese, sources] of Object.entries(table)) {
    for (const source of sources) {
      ARANESE_BY_SOURCE.set(source, aranese);
    }
  }
}

// Whole words only (\b does not work with accented letters), longest names first.
const NAMES = new RegExp(
  `(?<!\\p{L})(?:${[...ARANESE_BY_SOURCE.keys()]
    .sort((a, b) => b.length - a.length)
    .join("|")})(?!\\p{L})`,
  "giu"
);

const MONTH_NAMES = Object.keys(MONTHS).join("|");
const BEFORE_MONTH = new RegExp(
  `(?<!\\p{L})(?:de|d[’'])\\s*(${MONTH_NAMES})(?!\\p{L})`,
  "gu"
);
const BEFORE_YEAR = new RegExp(
  `(${MONTH_NAMES})\\s+(?:de|del|de l[’'])\\s*(\\d{4})`,
  "gu"
);

// Abbreviated weekday at the start of a formatted date ("vie, 24/04/2026, 17:00"): there are no official Aranese
// abbreviations, so the full Aranese day name is used.
const WEEKDAY_ABBREVIATIONS: Record<string, string> = {
  lun: "deluns", dl: "deluns", mon: "deluns",
  mar: "dimars", dt: "dimars", tue: "dimars",
  mié: "dimèrcles", dc: "dimèrcles", wed: "dimèrcles",
  jue: "dijaus", dj: "dijaus", thu: "dijaus",
  vie: "diuendres", dv: "diuendres", fri: "diuendres",
  sáb: "dissabte", ds: "dissabte", sat: "dissabte",
  dom: "dimenge", dg: "dimenge", sun: "dimenge"
};
const LEADING_ABBREVIATION = new RegExp(
  `^(${Object.keys(WEEKDAY_ABBREVIATIONS).join("|")})\\.?(?=,?\\s)`,
  "iu"
);

export function toAranese(text: string): string {
  return text
    .replace(
      LEADING_ABBREVIATION,
      abbreviation => WEEKDAY_ABBREVIATIONS[abbreviation.replace(".", "").toLowerCase()]
    )
    .replace(NAMES, name => ARANESE_BY_SOURCE.get(name.toLowerCase()) ?? name)
    .replace(BEFORE_MONTH, (_, month: string) =>
      /^[AEIOUaeiou]/.test(month) ? `d’${month}` : `de ${month}`
    )
    .replace(BEFORE_YEAR, "$1 deth $2");
}
